'use client';

import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Clock, Check, Save, Star, Edit2 } from 'lucide-react';

export interface ScheduleItem {
  id: string;
  dayOfWeek: number;
  dayName: string;
  enabled: boolean;
  startTime: string;
  endTime: string;
  breakStart?: string | null;
  breakEnd?: string | null;
}

export interface StaffItem {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  roleTitle?: string;
  avatarUrl?: string | null;
  colorHex: string;
  active: boolean;
  isFeatured?: boolean;
  schedules?: ScheduleItem[];
}

interface StaffScheduleCardsProps {
  staffList: StaffItem[];
  onToggleStaffActive: (staffId: string, currentActive: boolean) => void;
  onToggleStaffFeatured?: (staffId: string, currentFeatured: boolean) => void;
  onSaveStaffSchedules: (staffId: string, schedules: ScheduleItem[]) => Promise<void>;
  onEditStaff?: (staff: StaffItem) => void;
}

export function StaffScheduleCards({
  staffList,
  onToggleStaffActive,
  onToggleStaffFeatured,
  onSaveStaffSchedules,
  onEditStaff
}: StaffScheduleCardsProps) {
  const [expandedStaffId, setExpandedStaffId] = useState<string | null>(null);
  const [editingSchedules, setEditingSchedules] = useState<Record<string, ScheduleItem[]>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedSuccessId, setSavedSuccessId] = useState<string | null>(null);

  // Días estándar en orden Lunes (1) a Domingo (0)
  const dayLabels = [
    { num: 1, label: 'Lu' },
    { num: 2, label: 'Ma' },
    { num: 3, label: 'Mi' },
    { num: 4, label: 'Ju' },
    { num: 5, label: 'Vi' },
    { num: 6, label: 'Sá' },
    { num: 0, label: 'Do' },
  ];

  const toggleExpand = (staff: StaffItem) => {
    if (expandedStaffId === staff.id) {
      setExpandedStaffId(null);
    } else {
      setExpandedStaffId(staff.id);
      // Inicializar copia editable de schedules
      if (!editingSchedules[staff.id]) {
        setEditingSchedules(prev => ({
          ...prev,
          [staff.id]: staff.schedules ? JSON.parse(JSON.stringify(staff.schedules)) : []
        }));
      }
    }
  };

  const handleScheduleDayToggle = (staffId: string, dayOfWeek: number) => {
    setEditingSchedules(prev => {
      const currentList = prev[staffId] || [];
      const updated = currentList.map(sc => {
        if (sc.dayOfWeek === dayOfWeek) {
          return { ...sc, enabled: !sc.enabled };
        }
        return sc;
      });
      return { ...prev, [staffId]: updated };
    });
  };

  const handleTimeChange = (
    staffId: string,
    dayOfWeek: number,
    field: 'startTime' | 'endTime' | 'breakStart' | 'breakEnd',
    val: string
  ) => {
    setEditingSchedules(prev => {
      const currentList = prev[staffId] || [];
      const updated = currentList.map(sc => {
        if (sc.dayOfWeek === dayOfWeek) {
          return { ...sc, [field]: val };
        }
        return sc;
      });
      return { ...prev, [staffId]: updated };
    });
  };

  const handleSave = async (staffId: string) => {
    const list = editingSchedules[staffId];
    if (!list) return;

    setSavingId(staffId);
    try {
      await onSaveStaffSchedules(staffId, list);
      setSavedSuccessId(staffId);
      setTimeout(() => setSavedSuccessId(null), 3000);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 font-medium">
        Cuándo atiende cada profesional.
      </p>

      {/* Cards Container */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm divide-y divide-slate-100 overflow-hidden">
        {staffList.map((staff) => {
          const isExpanded = expandedStaffId === staff.id;
          const currentStaffSchedules = editingSchedules[staff.id] || staff.schedules || [];

          // Mapa rápido de días habilitados
          const enabledDaysMap = new Set(
            (staff.schedules || []).filter(s => s.enabled).map(s => s.dayOfWeek)
          );

          return (
            <div key={staff.id} className="transition-colors">
              {/* Staff Row Header */}
              <div
                onClick={() => toggleExpand(staff)}
                className="flex items-center justify-between px-6 py-4 hover:bg-slate-50/60 cursor-pointer select-none transition-colors"
              >
                {/* Left: Avatar, Name, Day Pills */}
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  {staff.avatarUrl ? (
                    <img
                      src={staff.avatarUrl}
                      alt={staff.name}
                      className="w-10 h-10 rounded-full object-cover shrink-0 shadow-xs border border-slate-200"
                    />
                  ) : (
                    <div
                      style={{ backgroundColor: staff.colorHex || '#10b981' }}
                      className="w-10 h-10 rounded-full text-white font-bold text-sm flex items-center justify-center shadow-xs shrink-0"
                    >
                      {staff.name.charAt(0)}
                    </div>
                  )}

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="text-sm font-bold text-slate-900">
                        {staff.name}
                      </h3>
                      {staff.isFeatured && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200/90 rounded text-[9px] font-bold">
                          <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
                          <span>Destacado en catálogo</span>
                        </span>
                      )}
                    </div>

                    {/* Day Pills */}
                    <div className="flex items-center gap-1.5">
                      {dayLabels.map(({ num, label }) => {
                        const isDayEnabled = enabledDaysMap.has(num);
                        return (
                          <span
                            key={num}
                            className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all ${
                              isDayEnabled
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-2xs'
                                : 'bg-slate-50 text-slate-400 border border-slate-200'
                            }`}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right: Featured Button, Switch Toggle, Divider, Chevron */}
                <div className="flex items-center gap-3 sm:gap-4" onClick={(e) => e.stopPropagation()}>
                  {/* Destacado Star Toggle Button */}
                  {onToggleStaffFeatured && (
                    <button
                      type="button"
                      onClick={() => onToggleStaffFeatured(staff.id, Boolean(staff.isFeatured))}
                      title={staff.isFeatured ? 'Quitar de destacados' : 'Marcar como profesional sugerido / destacado (aparece con estrella en el catálogo)'}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold transition-all border cursor-pointer ${
                        staff.isFeatured
                          ? 'bg-amber-50 text-amber-800 border-amber-300 shadow-2xs hover:bg-amber-100'
                          : 'bg-white text-slate-400 border-slate-200 hover:text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <Star className={`w-3.5 h-3.5 ${staff.isFeatured ? 'fill-amber-500 text-amber-500' : 'text-slate-400'}`} />
                      <span className="hidden sm:inline">{staff.isFeatured ? 'Destacado' : 'Destacar'}</span>
                    </button>
                  )}

                  {/* Switch Toggle */}
                  <button
                    type="button"
                    onClick={() => onToggleStaffActive(staff.id, staff.active)}
                    title={staff.active ? 'Profesional activo' : 'Profesional inactivo'}
                    className={`w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-200 ease-in-out ${
                      staff.active ? 'bg-emerald-600' : 'bg-slate-300'
                    }`}
                  >
                    <div
                      className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                        staff.active ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>

                  {onEditStaff && (
                    <button
                      type="button"
                      onClick={() => onEditStaff(staff)}
                      title="Editar datos, horarios y servicios del profesional"
                      className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors border border-slate-200 cursor-pointer"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  )}

                  <div className="h-6 w-px bg-slate-200" />

                  <button
                    type="button"
                    onClick={() => toggleExpand(staff)}
                    className="p-1 rounded-lg text-slate-400 hover:text-slate-700 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Expanded Schedule Form */}
              {isExpanded && (
                <div className="px-6 py-5 bg-slate-50/70 border-t border-slate-100 space-y-4 animate-in fade-in">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Franjas horarias y días de atención de {staff.name}
                    </p>
                    <span className="text-xs text-slate-400">
                      Rol: {staff.roleTitle || 'Profesional'}
                    </span>
                  </div>

                  <div className="bg-white border border-slate-200/80 rounded-xl divide-y divide-slate-100 overflow-hidden shadow-2xs">
                    {currentStaffSchedules
                      .slice()
                      .sort((a, b) => {
                        // Ordenar 1..6, 0 al final
                        const orderA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek;
                        const orderB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek;
                        return orderA - orderB;
                      })
                      .map((sc) => (
                        <div
                          key={sc.dayOfWeek}
                          className={`p-3 sm:px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                            sc.enabled ? 'bg-white' : 'bg-slate-50/50 opacity-60'
                          }`}
                        >
                          {/* Day toggle */}
                          <div className="flex items-center gap-3 w-36">
                            <input
                              type="checkbox"
                              id={`day_${staff.id}_${sc.dayOfWeek}`}
                              checked={sc.enabled}
                              onChange={() => handleScheduleDayToggle(staff.id, sc.dayOfWeek)}
                              className="w-4 h-4 text-emerald-600 rounded-md border-slate-300 focus:ring-emerald-500 cursor-pointer"
                            />
                            <label
                              htmlFor={`day_${staff.id}_${sc.dayOfWeek}`}
                              className="text-xs font-bold text-slate-900 cursor-pointer select-none"
                            >
                              {sc.dayName}
                            </label>
                          </div>

                          {/* Hours */}
                          {sc.enabled ? (
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="text-slate-400 font-medium">De</span>
                              <input
                                type="time"
                                value={sc.startTime}
                                onChange={(e) =>
                                  handleTimeChange(staff.id, sc.dayOfWeek, 'startTime', e.target.value)
                                }
                                className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800"
                              />
                              <span className="text-slate-400 font-medium">a</span>
                              <input
                                type="time"
                                value={sc.endTime}
                                onChange={(e) =>
                                  handleTimeChange(staff.id, sc.dayOfWeek, 'endTime', e.target.value)
                                }
                                className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800"
                              />

                              {/* Almuerzo / Pausa */}
                              <span className="text-slate-400 font-medium ml-2">Almuerzo:</span>
                              <input
                                type="time"
                                value={sc.breakStart || ''}
                                onChange={(e) =>
                                  handleTimeChange(staff.id, sc.dayOfWeek, 'breakStart', e.target.value)
                                }
                                placeholder="--:--"
                                className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700"
                              />
                              <span className="text-slate-400 font-medium">-</span>
                              <input
                                type="time"
                                value={sc.breakEnd || ''}
                                onChange={(e) =>
                                  handleTimeChange(staff.id, sc.dayOfWeek, 'breakEnd', e.target.value)
                                }
                                placeholder="--:--"
                                className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700"
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              Cerrado / Día libre
                            </span>
                          )}
                        </div>
                      ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      disabled={savingId === staff.id}
                      onClick={() => handleSave(staff.id)}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-1.5"
                    >
                      {savedSuccessId === staff.id ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>¡Horarios guardados!</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>{savingId === staff.id ? 'Guardando...' : 'Guardar Horarios'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
